import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Category, Post } from '@nhanho/shared'
import { formatDateVN } from '@nhanho/shared'
import { posts as api, categories as categoriesApi, explainError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useList } from '../../lib/useList'
import { useDebounced } from '../../lib/useDebounced'
import { useToast } from '../../lib/toast'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Pagination } from '../../components/ui/Pagination'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { ConfirmDialog } from '../../components/ui/Modal'

export default function PostList() {
  // Cố định trước, URL param sau — viết ngược thì dropdown lọc trạng thái mất tác dụng.
  const { data, loading, params, set, refresh } = useList<Post>((p) => api.list({ published: 'all', populate: 1, limit: 20, sort: '-updatedAt', ...p }))
  /**
   * `POST /api/posts/bulk` là `requireAdmin` — một lệnh xoá tới 200 bài. Không gác ở đây
   * thì editor/manager thấy cả ô chọn lẫn 3 nút (**kể cả Đăng/Ẩn**) rồi bấm là ăn 403.
   */
  const { isAdmin } = useAuth()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirmDel, setConfirmDel] = useState(false)
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const [blogCats, setBlogCats] = useState<Category[]>([])
  useEffect(() => {
    categoriesApi.list({ space: 'post', published: 'all', limit: 200, sort: 'order,name' })
      .then((r) => setBlogCats(r.docs))
      .catch(() => setBlogCats([]))
  }, [])
  const [q, setQ] = useState(String(params.search ?? ''))
  const dq = useDebounced(q, 300)
  useEffect(() => { if (dq !== String(params.search ?? '')) set({ search: dq }) }, [dq]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setSelected(new Set()), [data])

  const allChecked = !!data?.docs.length && data.docs.every((d) => selected.has(d.id))
  const bulk = async (action: 'publish' | 'unpublish' | 'delete') => {
    if (!selected.size) return
    if (action === 'delete' && !confirmDel) return setConfirmDel(true)
    setBusy(true)
    try { const r = await api.bulk([...selected], action); toast.success(`Đã cập nhật ${r.affected} bài`); setConfirmDel(false); refresh() }
    catch (e) { toast.error(explainError(e).message) } finally { setBusy(false) }
  }

  return (
    <>
      <PageHeader title="Bài viết" subtitle={data ? `${data.totalDocs} bài` : undefined} actions={<Link to="/posts/new"><Button>+ Bài viết mới</Button></Link>} />
      <div className="mb-4 grid gap-2 md:grid-cols-[1fr_200px_140px]">
        <Input placeholder="Tìm tiêu đề, slug, tag…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={String(params.category ?? '')} onChange={(e) => set({ category: e.target.value })}><option value="">Mọi chuyên mục</option>{blogCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>
        <Select value={String(params.published ?? 'all')} onChange={(e) => set({ published: e.target.value === 'all' ? null : e.target.value })}><option value="all">Tất cả</option><option value="true">Đã đăng</option><option value="false">Nháp</option></Select>
      </div>
      {isAdmin && selected.size > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-button bg-primary-100 px-3 py-2 text-sm">
          <span className="font-medium">{selected.size} đã chọn</span>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => bulk('publish')}>Đăng</Button>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => bulk('unpublish')}>Ẩn</Button>
          <Button size="sm" variant="danger" loading={busy} onClick={() => bulk('delete')}>Xoá</Button>
        </div>
      )}
      {loading && !data ? <Spinner /> : !data?.docs.length ? (
        <EmptyState title="Chưa có bài viết" action={<Link to="/posts/new"><Button>+ Bài viết mới</Button></Link>} />
      ) : (
        <div className="overflow-x-auto rounded-card border border-border bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-sand text-left text-xs uppercase tracking-wider text-ink-muted"><tr>
              {isAdmin && <th className="w-10 px-3 py-2.5"><input type="checkbox" checked={allChecked} onChange={() => setSelected(allChecked ? new Set() : new Set(data.docs.map((d) => d.id)))} aria-label="Chọn tất cả" /></th>}
              <th className="px-3 py-2.5">Bài viết</th><th className="px-3 py-2.5">Chuyên mục</th><th className="px-3 py-2.5">Tác giả</th><th className="px-3 py-2.5">Ngày đăng</th><th className="px-3 py-2.5">Trạng thái</th></tr></thead>
            <tbody className="divide-y divide-border">
              {data.docs.map((p) => {
                const img = typeof p.featuredImage === 'object' ? p.featuredImage : null
                return (
                  <tr key={p.id} className={selected.has(p.id) ? 'bg-primary-50' : 'hover:bg-sand/60'}>
                    {isAdmin && <td className="px-3 py-2"><input type="checkbox" checked={selected.has(p.id)} onChange={() => setSelected((s) => { const n = new Set(s); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n })} aria-label={`Chọn ${p.title}`} /></td>}
                    <td className="px-3 py-2"><Link to={`/posts/${p.id}`} className="flex items-center gap-3"><div className="h-11 w-16 shrink-0 overflow-hidden rounded-image bg-sand">{img && <img src={img.sizes?.thumbnail?.url ?? img.url} alt="" className="size-full object-cover" />}</div><div className="min-w-0"><div className="truncate font-medium text-ink hover:underline">{p.title}</div><div className="truncate text-xs text-ink-muted">/{p.slug}</div></div></Link></td>
                    <td className="px-3 py-2 text-ink-muted">{typeof p.category === 'object' && p.category ? p.category.name : '—'}</td>
                    {/*
                      `authorUser` là nguồn chân lý; chuỗi `author` cũ chỉ hiện cho bài chưa
                      migrate, kèm nhãn để người biên tập phân biệt được hai thứ.
                    */}
                    <td className="px-3 py-2 text-ink-muted">
                      {typeof p.authorUser === 'object' && p.authorUser
                        ? p.authorUser.name
                        : p.author?.trim()
                          ? <span title="Tác giả dạng chữ (bài cũ)">{p.author} <span className="text-ink-faint">(cũ)</span></span>
                          : '—'}
                    </td>
                    <td className="px-3 py-2 text-ink-muted">{formatDateVN(p.publishedAt) || '—'}</td>
                    <td className="px-3 py-2"><Badge tone={p.published ? 'success' : 'neutral'}>{p.published ? 'Đã đăng' : 'Nháp'}</Badge></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="border-t border-border px-3 py-2.5"><Pagination data={data} onPage={(p) => set({ page: p })} /></div>
        </div>
      )}
      <ConfirmDialog open={confirmDel} onCancel={() => setConfirmDel(false)} onConfirm={() => bulk('delete')} loading={busy} title="Xoá bài viết" message={<>Xoá <strong>{selected.size}</strong> bài đã chọn?</>} />
    </>
  )
}
